import React, { useCallback, ReactElement } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandIcon from '../../icons/expand-icon';
import FormatService from '../../services/format-service';
import useScreenSmall from '../../hooks/use-screen-small';
import useStylesExpansionGroup from './expansion-group.styles';

const ExpansionGroup: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isFixed,
    isExpanded,
    isScrollable,
    isTableGroup,
    isSecondary,
    isFullWidth,
    isDefaultExpanded,
    withOffset,
    withExpandedOffset,
    withNoHeader,
    className,
    detailsClassName,
    onChange,
    headerComponent,
    children,
  } = props;

  const { classes } = useStylesExpansionGroup();
  const isSmallScreen: boolean = useScreenSmall();

  const tableGroupClassName: string | undefined = isTableGroup && !isSmallScreen
    ? classes.expansionGroupTableGroup : undefined;
  const detailsTableGroupClassName: string | undefined = isTableGroup && !isSmallScreen
    ? classes.expansionGroupDetailsTableGroup : undefined;
  const detailsScrollableClassName: string | undefined = isScrollable && !isSmallScreen
    ? classes.expansionGroupDetailsScrollable : undefined;
  const headerFixedClassName: string | undefined = isFixed ? classes.expansionGroupHeaderFixed : undefined;
  const headerSecondaryClassName: string | undefined = isSecondary ? classes.expansionGroupHeaderSecondary : undefined;
  const headerExpandIconFixedClassName: string | undefined = isFixed
    ? classes.expansionGroupHeaderExpandIconFixed : undefined;
  const contentFixedClassName: string | undefined = isFixed ? classes.expansionGroupContentFixed : undefined;
  const offsetClassName: string | undefined = withOffset ? classes.expansionGroupOffset : undefined;
  const fullWidthClassName: string | undefined = isFullWidth ? classes.expansionGroupFullWidth : undefined;
  const expandedOffsetClassName: string | undefined = withExpandedOffset
    ? classes.expansionGroupExpandedOffset : undefined;

  const expansionGroupChange: ExpandPanelHandlerFunction = useCallback((event: React.SyntheticEvent): void => {
    if (onChange) {
      onChange(event);
    }
  }, [isExpanded]);

  return (
    <Accordion
      defaultExpanded={isDefaultExpanded}
      expanded={isExpanded}
      onChange={expansionGroupChange}
      className={FormatService.formatClassNames(
        tableGroupClassName, offsetClassName, fullWidthClassName, className,
      )}
      classes={{
        expanded: FormatService.formatClassNames(classes.expansionGroupExpanded, expandedOffsetClassName),
      }}
    >
      {
        !withNoHeader && (
          <AccordionSummary
            expandIcon={<ExpandIcon />}
            classes={{
              root: FormatService.formatClassNames(
                classes.expansionGroupHeader, headerFixedClassName, headerSecondaryClassName,
              ),
              content: FormatService.formatClassNames(classes.expansionGroupContent, contentFixedClassName),
              expandIconWrapper: FormatService.formatClassNames(
                classes.expansionGroupHeaderExpandIcon, headerExpandIconFixedClassName,
              ),
              expanded: classes.expansionGroupHeaderExpanded,
              focusVisible: classes.expansionGroupHeaderFocusVisible,
            }}
          >
            {headerComponent}
          </AccordionSummary>
        )
      }
      <AccordionDetails
        className={FormatService.formatClassNames(
          classes.expansionGroupDetails, detailsTableGroupClassName, detailsScrollableClassName, detailsClassName,
        )}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
});

export default ExpansionGroup;
